import {
  PolicyInput_PenaltyFrequencyEnum,
  Policy_FeeCalculationMethodEnum,
  Policy_DueDateKindEnum,
  Stops_LineTypeEnum,
  Offense_StatusEnum,
  PolicyAmount_StatusEnum,
  PolicyRule_RuleUnitsEnum,
  EventRecord_EventTypesEnum,
} from 'src/api/gen'

import { formatNumber } from './helpers'

// fake t function for translation collection
export const t = (k: string) => k

// 'inactive' is frontend-only, for devices garbage collected, not a backend EventType value
export type EventTypeKey = EventRecord_EventTypesEnum | 'inactive'

interface IEnumsTranslationKeys {
  [index: string]: { [index: string]: string }
  EventType: Record<EventTypeKey, string>
}

export const enumsTranslationKeys: IEnumsTranslationKeys = {
  AnomalyType: {
    // Legacy ES anomaly types (used by the OpenSearch-backed Technical compliance tab)
    event_out_of_order: t('kpi.AnomaliesPerType.event_out_of_order'),
    old_event: t('kpi.AnomaliesPerType.old_event'),
    no_event_for_too_long: t('kpi.AnomaliesPerType.no_event_for_too_long'),
    trip_workflow: t('kpi.AnomaliesPerType.trip_workflow'),
    workflow_trip: t('kpi.AnomaliesPerType.workflow_trip'),
    workflow: t('kpi.AnomaliesPerType.workflow'),
    duplicate: t('kpi.AnomaliesPerType.duplicate'),
    wrong_order: t('kpi.AnomaliesPerType.wrong_order'),
    teleportation: t('kpi.AnomaliesPerType.teleportation'),
    outside_city: t('kpi.AnomaliesPerType.outside_city'),
    unknown: t('kpi.AnomaliesPerType.unknown'),
    // dbt anomaly-type families (used by the Tiger-backed tab)
    invalid_geometry: t('kpi.AnomaliesPerType.invalid_geometry'),
    implausible_trip: t('kpi.AnomaliesPerType.implausible_trip'),
    incomplete_data: t('kpi.AnomaliesPerType.incomplete_data'),
    duplication: t('kpi.AnomaliesPerType.duplication'),
    state_transition: t('kpi.AnomaliesPerType.state_transition'),
    route_consistency: t('kpi.AnomaliesPerType.route_consistency'),
    outside_jurisdiction: t('kpi.AnomaliesPerType.outside_jurisdiction'),
    liveness: t('kpi.AnomaliesPerType.liveness'),
  },
  AuditLogKind: {
    area: t('area'),
    polygon: t('polygon'),
    user: t('user'),
    jurisdiction: t('jurisdiction'),
    policy: t('policy'),
    report: t('report'),
  },
  AuditLogType: {
    created: t('administration.auditLogs.created'),
    changed: t('administration.auditLogs.changed'),
    deleted: t('administration.auditLogs.deleted'),
  },
  DeviceType: {
    bicycle: t('device.bicycle'),
    car: t('device.car'),
    cargo_bicycle: t('device.cargo_bicycle'),
    delivery_bot: t('device.deliveryBot'),
    moped: t('device.moped'),
    other: t('device.other'),
    scooter: t('device.scooter'),
    pt: t('device.public_transport'),
    scooter_seated: t('device.scooter_seated'),
  },
  DeviceCategory: {
    bicycle_category: t('device.category.bicycle'),
    car_category: t('device.category.car'),
    cargo_bicycle_category: t('device.category.cargo_bicycle'),
    delivery_bot_category: t('device.category.deliveryBot'),
    moped_category: t('device.category.moped'),
    other_category: t('device.category.other'),
    scooter_category: t('device.category.scooter'),
    pt_category: t('device.category.public_transport'),
    scooter_seated_category: t('device.category.scooter_seated'),
  },
  DevicePropulsion: {
    combustion: t('devicePropulsion.combustion'),
    electric: t('devicePropulsion.electric'),
    electric_assist: t('devicePropulsion.electricAssist'),
    human: t('devicePropulsion.human'),
    missing: t('devicePropulsion.missing'),
  },
  EventType: {
    agency_drop_off: t('event.agency_drop_off'),
    agency_pick_up: t('event.agency_pick_up'),
    battery_charged: t('event.battery_charged'),
    battery_low: t('event.battery_low'),
    comms_lost: t('event.comms_lost'),
    comms_restored: t('event.comms_restored'),
    compliance_pick_up: t('event.compliance_pick_up'),
    decommissioned: t('event.decommissioned'),
    located: t('event.located'),
    maintenance: t('event.maintenance'),
    maintenance_pick_up: t('event.maintenance_pick_up'),
    missing: t('event.missing'),
    off_hours: t('event.off_hours'),
    on_hours: t('event.on_hours'),
    provider_drop_off: t('event.provider_drop_off'),
    rebalance_pick_up: t('event.rebalance_pick_up'),
    reservation_cancel: t('event.reservation_cancel'),
    reservation_start: t('event.reservation_start'),
    reservation_stop: t('event.reservation_stop'),
    system_resume: t('event.system_resume'),
    system_suspend: t('event.system_suspend'),
    trip_cancel: t('event.trip_cancel'),
    trip_end: t('event.trip_end'),
    trip_enter_jurisdiction: t('event.trip_enter_jurisdiction'),
    trip_leave_jurisdiction: t('event.trip_leave_jurisdiction'),
    trip_start: t('event.trip_start'),
    trip_stop: t('event.trip_stop'),
    unspecified: t('event.unspecified'),
    // Custom for devices garbage collected
    inactive: t('event.inactive'),
    // 2.x specific events
    changed_geographies: t('event.changed_geographies'),
    charging_end: t('event.charging_end'),
    charging_start: t('event.charging_start'),
    customer_cancellation: t('event.customer_cancellation'),
    driver_cancellation: t('event.driver_cancellation'),
    fueling_end: t('event.fueling_end'),
    fueling_start: t('event.fueling_start'),
    maintenance_end: t('event.maintenance_end'),
    not_located: t('event.not_located'),
    notification_sent: t('event.notification_sent'),
    order_drop_off: t('event.order_drop_off'),
    order_pick_up: t('event.order_pick_up'),
    passenger_cancellation: t('event.passenger_cancellation'),
    provider_cancellation: t('event.provider_cancellation'),
    recommission: t('event.recommission'),
    remote_end: t('event.remote_end'),
    remote_start: t('event.remote_start'),
    service_end: t('event.service_end'),
    service_start: t('event.service_start'),
    system_start: t('event.system_start'),
    trip_pause: t('event.trip_pause'),
    trip_resume: t('event.trip_resume'),
  },
  MaintenanceKinds: {
    on_street: t('maintenance_kinds.on_street'),
    warehouse: t('maintenance_kinds.warehouse'),
  },
  FeeCalculationMethod: {
    [Policy_FeeCalculationMethodEnum.highest]: t('fee_calculation_method-highest'),
    [Policy_FeeCalculationMethodEnum.lowest]: t('fee_calculation_method-lowest'),
    [Policy_FeeCalculationMethodEnum.sum]: t('fee_calculation_method-sum'),
    [Policy_FeeCalculationMethodEnum.mean]: t('fee_calculation_method-mean'),
    [Policy_FeeCalculationMethodEnum.origin]: t('fee_calculation_method-origin'),
    [Policy_FeeCalculationMethodEnum.destination]: t('fee_calculation_method-destination'),
  },
  FeeDueDate: {
    [Policy_DueDateKindEnum.first_day]: t('fee_due_date-first_day'),
    [Policy_DueDateKindEnum.last_day]: t('fee_due_date-last_day'),
  },
  PenaltySchemesStatus: {
    [PolicyAmount_StatusEnum.upcoming]: t('penaltySchemes.state.upcoming'),
    [PolicyAmount_StatusEnum.ongoing]: t('penaltySchemes.state.ongoing'),
    [PolicyAmount_StatusEnum.finished]: t('penaltySchemes.state.finished'),
  },
  ReportType: {
    area_flow: t('report.area_flow'),
    historical_map: t('report.historical_map'),
    effective_presence: t('report.effective_presence'),
  },
  PenaltyFrequency: {
    [PolicyInput_PenaltyFrequencyEnum.hourly]: t('policy.hourly_penalty'),
    [PolicyInput_PenaltyFrequencyEnum.daily]: t('policy.daily_penalty'),
  },
  Time: {
    hour: t('hour'),
    day: t('day'),
    week: t('week'),
    month: t('month'),
    quarter: t('quarter'),
  },
  RecurrenceKind: {
    day: t('day'),
    week: t('week'),
    month: t('month'),
    year: t('year'),
  },
  Kpi: {
    peak: t('kpi.peak'),
  },
  PolicyKind: {
    absolute_fleet_size: t('policy.kind.absolute_fleet_size'),
    fleet_budget: t('policy.kind.fleet_budget'),
    permit: t('policy.kind.permit'),
    geofence: t('policy.kind.geofence'),
    speed: t('policy.kind.speed'),
    parking: t('policy.kind.parking'),
    abandoned: t('policy.kind.abandoned'),
    trip_taxation: t('policy.kind.trip_taxation'),
    sidewalk: t('policy.kind.sidewalk'),
    balancing: t('policy.kind.balancing'),
    time: t('policy.kind.time'),
    beginner_speed_limit: t('policy.kind.beginner_speed_limit'),
    beginner_allowed_hours: t('policy.kind.beginner_allowed_hours'),
    vehicle_deployment: t('policy.kind.vehicle_deployment'),
    maintenance: t('policy.kind.maintenance'),
    dwell_time: t('policy.kind.dwell_time'),
  },
  PolicyStatus: {
    draft: t('policy.status.draft'),
    upcoming: t('policy.status.upcoming'),
    ongoing: t('policy.status.ongoing'),
    finished: t('policy.status.finished'),
    cancelled: t('policy.status.cancelled'),
  },
  TripType: {
    delivery: t('tripType.delivery'),
    return: t('tripType.return'),
    advertising: t('tripType.advertising'),
    mapping: t('tripType.mapping'),
    roaming: t('tripType.roaming'),
    rider: t('tripType.rider'),
    rebalance: t('tripType.rebalance'),
    maintenance: t('tripType.maintenance'),
    private: t('tripType.private'),
    shared: t('tripType.shared'),
    empty: t('tripType.empty'),
    reservation: t('tripType.reservation'),
    unspecified: t('tripType.unspecified'),
  },
  VehicleStates: {
    removed: t('vehiclestates.removed'),
    available: t('vehiclestates.available'),
    non_operational: t('vehiclestates.non_operational'),
    reserved: t('vehiclestates.reserved'),
    on_trip: t('vehiclestates.on_trip'),
    elsewhere: t('vehiclestates.elsewhere'),
    unknown: t('vehiclestates.unknown'),
    // 2.x states
    stopped: t('vehiclestates.stopped'),
    non_contactable: t('vehiclestates.non_contactable'),
    missing: t('vehiclestates.missing'),
  },
  OffenseReason: {
    under_minimum: t('policy.dashboard.offense.reason.minimum'),
    over_maximum: t('policy.dashboard.offense.reason.maximum'),
  },
  LineType: {
    [Stops_LineTypeEnum.train]: t('stop_types.train'),
    [Stops_LineTypeEnum.tram]: t('stop_types.tram'),
    [Stops_LineTypeEnum.subway]: t('stop_types.subway'),
    [Stops_LineTypeEnum.bike]: t('stop_types.bike'),
    [Stops_LineTypeEnum.bus]: t('stop_types.bus'),
  },
  OffensesStatus: {
    [Offense_StatusEnum.agent_to_check]: t('policy.dashboard.offense.status.agent_to_check'),
    [Offense_StatusEnum.auto_confirmed]: t('policy.dashboard.offense.status.auto_confirmed'),
    [Offense_StatusEnum.confirmed_by_agent]: t('policy.dashboard.offense.status.confirmed_by_agent'),
    [Offense_StatusEnum.invalidated]: t('policy.dashboard.offense.status.invalidated'),
  },
  Unit: {
    [PolicyRule_RuleUnitsEnum.days]: t('days'),
    [PolicyRule_RuleUnitsEnum.km]: t('kilometers'),
    [PolicyRule_RuleUnitsEnum.miles]: t('miles'),
  },
}

// Event types reach the frontend as plain strings (the generated client types them as `string`,
// see the DRF ChoiceField caveat on AgencySettingsSerializer.vehicle_state_events), so narrow
// before indexing; unknown values are displayed raw.
const isEventTypeKey = (value: string): value is EventTypeKey =>
  Object.prototype.hasOwnProperty.call(enumsTranslationKeys.EventType, value)

export const eventTypeTranslationKey = (eventType: string): string =>
  isEventTypeKey(eventType) ? enumsTranslationKeys.EventType[eventType] : eventType

export interface ICurrencySymbols {
  CZK: string
  EUR: string
  GBP: string
  SAR: string
  USD: string
}
export const currencySymbols: ICurrencySymbols = {
  CZK: 'Kč',
  EUR: '€',
  GBP: '£',
  SAR: 'ر.س',
  USD: '$',
}
export const PREFIXED_CURRENCIES = ['USD', 'SAR']
export const PREFIXED_CURRENCY_SYMBOLS = ['$', 'ر.س']

export const withFormattedCount = (count: number, locale?: string) => ({
  context: 'formatted',
  count,
  formattedCount: formatNumber(count, locale),
})
